import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { supabase } from "../../services/supabase";
import { useToast } from "../../components/ui/Toast";
import { LoadingOverlay } from "../../components/ui/Loading";

interface BusinessApplication {
  id: number;
  business_name: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  services: string[];
  created_at: string;
  logo_url?: string;
  is_active: boolean;
  verified?: boolean;
  user_id?: string;
  rejection_reason?: string;
}

const mockBusinesses: BusinessApplication[] = [
  {
    id: 1,
    business_name: "TyrePro Johannesburg",
    contact_email: "info@tyrepro.co.za",
    contact_phone: "+27 11 123 4567",
    address: "123 Main Rd, Johannesburg, 2001",
    services: ["Tyre Fitting", "Wheel Alignment", "Balancing"],
    created_at: "2025-07-20T10:00:00Z",
    logo_url: "",
    is_active: false,
    verified: false,
  },
  {
    id: 2,
    business_name: "WheelWorks Cape Town",
    contact_email: "hello@wheelworks.co.za",
    contact_phone: "+27 21 987 6543",
    address: "456 Side St, Cape Town, 8001",
    services: ["Tyre Sales", "Balancing", "Repairs"],
    created_at: "2025-07-18T11:30:00Z",
    logo_url: "",
    is_active: false,
    verified: false,
  },
];

function formatDate(dateString: string | undefined) {
  if (!dateString) return "Unknown";
  return new Date(dateString).toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const AdminBusinessApprovalPage: React.FC = () => {
  const [pendingBusinesses, setPendingBusinesses] = useState<BusinessApplication[]>(mockBusinesses);
  const [loading, setLoading] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const { success: showSuccessToast, error: showErrorToast } = useToast();

  useEffect(() => {
    fetchPendingBusinesses();
  }, []);

  const fetchPendingBusinesses = async () => {
    try {
      setLoading(true);
      
      // First try to get actual business applications
      const { data: businessData } = await (supabase as any)
        .from("businesses")
        .select("*")
        .eq("verified", false)
        .order("created_at", { ascending: false });

      if (businessData && businessData.length > 0) {
        setPendingBusinesses(businessData);
      } else {
        // If no businesses found, check for users who registered but don't have business records
        const { data: userData } = await supabase.auth.admin.listUsers();
        
        if (userData?.users) {
          const pendingUsers = userData.users
            .filter(user => user.user_metadata?.businessName)
            .map((user, index) => ({
              id: index + 1000, // Temporary ID for users without business records
              business_name: user.user_metadata.businessName || "Unknown Business",
              contact_email: user.email || "",
              contact_phone: user.user_metadata.contactPhone || "",
              address: user.user_metadata.address || "",
              services: user.user_metadata.services || [],
              created_at: user.created_at || "",
              logo_url: "",
              is_active: false,
              verified: false,
              user_id: user.id
            }));
          setPendingBusinesses(pendingUsers);
        } else {
          // Fallback to mock data
          setPendingBusinesses(mockBusinesses);
        }
      }
    } catch (error) {
      console.error("Error fetching pending businesses:", error);
      setPendingBusinesses(mockBusinesses);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (business: BusinessApplication) => {
    try {
      setLoading(true);
      
      console.log("🔍 DEBUG: Starting approval process for:", {
        businessId: business.id,
        businessName: business.business_name,
        contactEmail: business.contact_email,
        currentVerified: business.verified
      });
      
      // First check if business exists, if not create it
      const { data: existingBusiness, error: selectError } = await (supabase as any)
        .from("businesses")
        .select("id, verified, business_status")
        .eq("contact_email", business.contact_email)
        .single();

      console.log("🔍 DEBUG: Existing business query result:", {
        data: existingBusiness,
        error: selectError
      });

      if (!existingBusiness) {
        console.log("🔍 DEBUG: No existing business found, creating new record");
        
        // Create the business record if it doesn't exist
        const { data: newBusiness, error: createError } = await (supabase as any)
          .from("businesses")
          .insert({
            name: business.business_name,
            contact_email: business.contact_email,
            contact_phone: business.contact_phone,
            address: business.address,
            services: business.services,
            logo_url: business.logo_url || "",
            verified: true,
            business_status: "active",
            owner_id: business.user_id || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select();

        console.log("🔍 DEBUG: Business creation result:", {
          data: newBusiness,
          error: createError
        });

        if (createError) {
          console.error("❌ ERROR: Failed to create business:", createError);
          throw createError;
        }
        
        console.log("✅ SUCCESS: Business created with verified=true");
      } else {
        console.log("🔍 DEBUG: Existing business found, updating record");
        console.log("🔍 DEBUG: Current business state:", existingBusiness);
        
        // Update existing business status to active
        const { data: updatedBusiness, error: updateError } = await (supabase as any)
          .from("businesses")
          .update({
            verified: true,
            business_status: "active",
            updated_at: new Date().toISOString()
          })
          .eq("id", existingBusiness.id)
          .select();

        console.log("🔍 DEBUG: Business update result:", {
          data: updatedBusiness,
          error: updateError,
          updateQuery: {
            verified: true,
            business_status: "active",
            whereId: existingBusiness.id
          }
        });

        if (updateError) {
          console.error("❌ ERROR: Failed to update business:", updateError);
          throw updateError;
        }
        
        // Verify the update was successful
        const { data: verificationCheck, error: verifyError } = await (supabase as any)
          .from("businesses")
          .select("id, verified, business_status")
          .eq("id", existingBusiness.id)
          .single();
          
        console.log("🔍 DEBUG: Post-update verification check:", {
          data: verificationCheck,
          error: verifyError
        });
        
        if (verificationCheck?.verified !== true) {
          console.error("❌ CRITICAL: Verification status not updated properly!", verificationCheck);
        } else {
          console.log("✅ SUCCESS: Business verified status updated to true");
        }
      }

      // Remove from pending list
      setPendingBusinesses(prev => prev.filter(b => b.id !== business.id));
      
      showSuccessToast({
        title: "Business Approved",
        message: `${business.business_name} has been approved and activated.`
      });

      // Send notification email to business
      await supabase.functions.invoke("send-business-notification", {
        body: JSON.stringify({
          to: business.contact_email,
          subject: "Your business has been approved",
          message: `Congratulations, ${business.business_name} has been approved and activated on TyreLink.`
        })
      });
      console.log(`Approval notification sent to ${business.contact_email}`);

    } catch (error) {
      console.error("Error approving business:", error);
      showErrorToast("Failed to approve business. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = (business: BusinessApplication) => {
    setSelectedBusiness(business);
    setShowRejectionModal(true);
  };

  const confirmReject = async () => {
    if (!selectedBusiness) return;

    try {
      setLoading(true);
      
      // Update business with rejection reason and remove/flag as rejected
      const { error: updateError } = await (supabase as any)
        .from("businesses")
        .update({
          rejection_reason: rejectionReason,
          verified: false,
          business_status: "inactive",
          updated_at: new Date().toISOString()
        })
        .eq("id", selectedBusiness.id);

      if (updateError) {
        throw updateError;
      }

      // Remove from pending list
      setPendingBusinesses(prev => prev.filter(b => b.id !== selectedBusiness.id));
      
      showSuccessToast({
        title: "Business Rejected",
        message: `${selectedBusiness.business_name} application has been rejected.`
      });

      // Send rejection notification email to business
      await supabase.functions.invoke("send-business-notification", {
        body: JSON.stringify({
          to: selectedBusiness.contact_email,
          subject: "Your business application was rejected",
          message: `Unfortunately, your application for ${selectedBusiness.business_name} was rejected. Reason: ${rejectionReason}`
        })
      });
      console.log(`Rejection notification sent to ${selectedBusiness.contact_email} with reason: ${rejectionReason}`);

      setShowRejectionModal(false);
      setSelectedBusiness(null);
      setRejectionReason("");

    } catch (error) {
      console.error("Error rejecting business:", error);
      showErrorToast("Failed to reject business. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const requestMoreInfo = async (business: BusinessApplication) => {
    try {
      // Send request for more info email to business
      await supabase.functions.invoke("send-business-notification", {
        body: JSON.stringify({
          to: business.contact_email,
          subject: "More information required for your business application",
          message: `Dear ${business.business_name}, please provide additional information to complete your application.`
        })
      });
      showSuccessToast({
        title: "Information Requested",
        message: `More information has been requested from ${business.business_name}.`
      });
      console.log(`Requested more info from ${business.contact_email}`);
    } catch (error) {
      console.error("Error requesting more info:", error);
      showErrorToast("Failed to request more information.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h1 className="text-3xl font-bold text-gray-900">Business Applications</h1>
            <p className="text-gray-600 mt-2">Review and manage pending business registrations</p>
            <div className="mt-4 flex items-center space-x-4">
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                {pendingBusinesses.length} Pending Reviews
              </div>
              <button
                onClick={fetchPendingBusinesses}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          {/* Applications Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Services</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingBusinesses.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12">
                        <div className="text-gray-500">
                          <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-lg font-medium">No pending applications</p>
                          <p className="text-sm text-gray-400">All business applications have been processed</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pendingBusinesses.map((business) => (
                      <tr key={business.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{business.business_name}</div>
                            <div className="text-sm text-gray-500">{business.address}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{business.contact_email}</div>
                          <div className="text-sm text-gray-500">{business.contact_phone}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {business.services.slice(0, 2).join(", ")}
                            {business.services.length > 2 && ` +${business.services.length - 2} more`}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(business.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleApprove(business)}
                              disabled={loading}
                              className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(business)}
                              disabled={loading}
                              className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                              Reject
                            </button>
                            <button
                              onClick={() => requestMoreInfo(business)}
                              disabled={loading}
                              className="bg-yellow-600 text-white px-3 py-1 rounded text-xs hover:bg-yellow-700 transition-colors disabled:opacity-50"
                            >
                              More Info
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* Rejection Modal */}
        {showRejectionModal && selectedBusiness && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Reject Application</h3>
              <p className="text-gray-600 mb-4">
                Please provide a reason for rejecting {selectedBusiness.business_name}'s application:
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 mb-4 h-24 resize-none"
                placeholder="Enter rejection reason..."
                required
              />
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowRejectionModal(false);
                    setSelectedBusiness(null);
                    setRejectionReason("");
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReject}
                  disabled={!rejectionReason.trim() || loading}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? "Rejecting..." : "Reject Application"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <LoadingOverlay isVisible={loading} message="Processing application..." />
    </div>
  );
};

export default AdminBusinessApprovalPage;